import { Route, Routes } from '@angular/router';
import {
  LstAuthGuard,
  LstCallbackComponent,
  LstErrorComponent,
  LstLogoutComponent,
} from '@savvaslearning/lst-auth';

import { AUTH_CALLBACK_ROUTE, AUTH_ERROR_ROUTE } from './core/auth/auth.constants';
import {
  requireLegacyBackend,
  requireAuth,
  redirectToRoleLanding,
  requireGroupsAccess,
  requireClassSelected,
  requireLegacyToken,
  requireProjectionClassSelected,
  requireProjectionTeacher,
  requireProgramResolved,
  requireStudent,
  requireTabAccess,
  requireTeacher,
} from './core/guards/route.guards';

// SSO returns to AUTH_CALLBACK_ROUTE, a value the auth gateway has registered, so the path
// cannot move on our side alone. Failures land on lst-auth's own 'auth-error', which
// LST_MODULE_OPTIONS.errorRoute points at.
const authRoutes: Routes = [
  {
    path: AUTH_CALLBACK_ROUTE,
    loadComponent: () =>
      import('./core/auth/auth-callback/auth-callback.component').then(
        (m) => m.AuthCallbackComponent,
      ),
  },
  { path: 'auth-callback', component: LstCallbackComponent },
  { path: AUTH_ERROR_ROUTE, component: LstErrorComponent },
  // lst-auth ships this to log out by navigation; we call LogoutService directly, but an
  // external link may still use it.
  { path: 'logout', component: LstLogoutComponent },
];

// Reached before a class is chosen.
const entryRoutes: Routes = [
  {
    // Legacy pastes a TokenID here; skipped entirely on the new backend.
    path: 'token-entry',
    canActivate: [requireLegacyBackend],
    loadComponent: () =>
      import('./features/token-entry/token-entry.component').then((m) => m.TokenEntryComponent),
  },
  {
    // Shown when the normal flow is opened without a program context.
    path: 'program-error',
    loadComponent: () =>
      import('./features/program-error/program-error.component').then(
        (m) => m.ProgramErrorComponent,
      ),
  },
  {
    // requireProgramResolved runs before requireLegacyToken so the launch params
    // aren't lost to the token-entry redirect.
    path: 'class-picker',
    canActivate: [requireAuth, requireProgramResolved, requireLegacyToken],
    loadChildren: () =>
      import('./features/class-picker/class-picker.routes').then((m) => m.classPickerRoutes),
  },
];

// Outside the shell: the EPUB presenter reads the RBS session directly, so these take
// LstAuthGuard rather than the shell's chain. Both are external entry points.
const readerRoutes: Routes = [
  {
    path: 'library/reader/:readerBookId',
    canActivate: [LstAuthGuard],
    loadComponent: () =>
      import('./features/library/reader/library-reader.component').then(
        (m) => m.LibraryReaderComponent,
      ),
  },
  {
    path: 'library/book/:readerBookId',
    canActivate: [LstAuthGuard],
    loadComponent: () =>
      import('./features/library/reader/library-reader.component').then(
        (m) => m.LibraryReaderComponent,
      ),
  },
];

// Outside the shell, but still part of this Angular app: an existing signed-in/class-selected
// session may open it directly, while a fresh browser is sent through auth and class-picker.
const projectionRoute: Route = {
  path: 'projection',
  data: { appMode: 'projection' },
  canActivate: [
    requireAuth,
    requireLegacyToken,
    requireProjectionTeacher,
    requireProgramResolved,
    requireProjectionClassSelected,
  ],
  loadComponent: () =>
    import('./features/projection-receiver/projection-receiver.component').then(
      (m) => m.ProjectionReceiverComponent,
    ),
};

const shellRoute: Route = {
  // Post-auth shell: wraps each tab's component with cv-bottom-nav so the
  // nav stays mounted across navigations (mirrors legacy footer + iframe).
  path: '',
  canActivate: [requireAuth, requireProgramResolved, requireLegacyToken, requireClassSelected],
  loadComponent: () =>
    import('./app-shell/cv-app-shell.component').then((m) => m.CvAppShellComponent),
  children: [
    {
      path: '',
      pathMatch: 'full',
      redirectTo: redirectToRoleLanding,
    },
    {
      path: 'organizer',
      canActivate: [requireTeacher, requireTabAccess],
      loadChildren: () =>
        import('./features/organizer/organizer.routes').then((m) => m.organizerRoutes),
    },
    {
      path: 'planner',
      canActivate: [requireTeacher, requireTabAccess],
      loadComponent: () =>
        import('./features/planner/planner.component').then((m) => m.PlannerComponent),
    },
    {
      path: 'lessons',
      canActivate: [requireTeacher, requireTabAccess],
      loadChildren: () => import('./features/lessons/lessons.routes').then((m) => m.lessonsRoutes),
    },
    {
      path: 'assignments',
      title: 'Assignment',
      canActivate: [requireTabAccess],
      loadChildren: () =>
        import('./features/assignments/assignments.routes').then((m) => m.assignmentsRoutes),
    },
    {
      path: 'performance',
      canActivate: [requireTeacher],
      loadChildren: () =>
        import('./features/performance/performance.routes').then((m) => m.performanceRoutes),
    },
    {
      path: 'review',
      title: 'Review',
      canActivate: [requireStudent],
      loadComponent: () =>
        import('./features/library/book-review/book-review.component').then(
          (m) => m.BookReviewComponent,
        ),
    },
    {
      path: 'notebook',
      title: 'Notebook',
      canActivate: [requireStudent],
      loadComponent: () =>
        import('./features/notebook/notebook-shell.component').then(
          (m) => m.NotebookShellComponent,
        ),
    },
    {
      path: 'notebook/student/:studentId',
      title: 'Notebook',
      canActivate: [requireTeacher],
      data: { notebookMode: 'instructor' },
      loadComponent: () =>
        import('./features/notebook/notebook-shell.component').then(
          (m) => m.NotebookShellComponent,
        ),
    },
    {
      path: 'library',
      title: 'Library',
      loadComponent: () =>
        import('./features/library/library.component').then((m) => m.LibraryComponent),
    },
    {
      path: 'groups',
      canActivate: [requireGroupsAccess],
      loadComponent: () =>
        import('./features/groups/groups.component').then((m) => m.GroupsComponent),
    },
    {
      path: 'connect/poll',
      title: 'Connect',
      canActivate: [requireTeacher],
      data: { connectMode: 'poll' },
      loadComponent: () =>
        import('./features/connect/teacher-connect-route.component').then(
          (m) => m.TeacherConnectRouteComponent,
        ),
    },
    {
      path: 'connect/buzz',
      title: 'Connect',
      canActivate: [requireTeacher],
      data: { connectMode: 'buzz' },
      loadComponent: () =>
        import('./features/connect/teacher-connect-route.component').then(
          (m) => m.TeacherConnectRouteComponent,
        ),
    },
    {
      path: 'connect',
      title: 'Connect',
      canActivate: [requireStudent],
      loadComponent: () =>
        import('./features/connect/student-connect.component').then(
          (m) => m.StudentConnectComponent,
        ),
    },
  ],
};

export const routes: Routes = [
  ...authRoutes,
  ...entryRoutes,
  ...readerRoutes,
  projectionRoute,
  shellRoute,
  { path: '**', redirectTo: '' },
];
